import { isEmptyString } from '@/utils/string';
import type { LoginStep } from '../login-flow';

export type StepPrerequisites = {
  email: string;
  ssoConfiguration: unknown;
  chooseUserEntries: readonly unknown[];
  /** True once the email step has actually been submitted this session (SSO discovery ran). */
  hasSubmittedEmail: boolean;
  /** Set by a "2FA required" login response; the only state the 2FA step needs. */
  twoFactorAuthLoginToken: string;
};

/**
 * Password / SSO / chooser / 2FA depend on state established earlier in the
 * flow, which only lives in memory. On a refresh or direct visit to e.g.
 * `/login/sso` that state is gone, so fall back to the email step instead of
 * rendering a blank or unusable panel. The 2FA email-link step is exempt — it's
 * reachable directly via the magic link's `?token=`.
 *
 * Each step guards on the state *it* actually needs (mirroring Ember, where
 * each sub-route had its own `redirect()` guard):
 *
 * - **password** requires the email step to have actually been submitted this
 *   session (`hasSubmittedEmail`). A prefilled `?email=` seeds the email in
 *   memory but is *not* a submission — so a direct visit to
 *   `/login/password?email=…` falls back to the email step (which auto-submits
 *   and runs SSO discovery) rather than stranding an SSO-only account on the
 *   password form. And when an SSO configuration is in memory, the password
 *   step redirects to `sso`: SSO accounts never see a password form.
 *
 * - **two_factor_authentication** requires only the 2FA login token from a
 *   "2FA required" response (Ember guarded on exactly this). It must not
 *   require email-step state: Google One-Click reaches 2FA without ever
 *   submitting the email form (requiring it looped Google + 2FA users back to
 *   the email step — FPS-2523). Nor may it bounce to `sso` while an SSO config
 *   is in memory — 2FA follows a successful first factor, including a Google
 *   SSO one.
 */
export function resolveEffectiveStep(
  step: LoginStep,
  { email, ssoConfiguration, chooseUserEntries, hasSubmittedEmail, twoFactorAuthLoginToken }: StepPrerequisites,
): LoginStep {
  switch (step) {
    case 'password':
      if (!hasSubmittedEmail || isEmptyString(email)) return 'email';
      return ssoConfiguration == null ? step : 'sso';
    case 'two_factor_authentication':
      return isEmptyString(twoFactorAuthLoginToken) ? 'email' : step;
    case 'sso':
      return ssoConfiguration == null ? 'email' : step;
    case 'choose_user':
      return chooseUserEntries.length === 0 ? 'email' : step;
    default:
      return step;
  }
}
